import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Copy,
  Check,
  Users,
  QrCode,
  Wifi,
  WifiOff,
  RefreshCw,
  Eye,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  Stack,
  LinearProgress,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { getAuthenticatedWSUrl, API_KEY, API_BASE_URL } from "../api.js";
import { usePostHog } from "../Posthogprovider.jsx";

// ---------- Styled Components ----------
const Container = styled(Paper)(({ theme }) => ({
  width: "100%",
  height: "100vh",
  margin: "0 auto",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

// NEW: Demo Mode Banner
const DemoBanner = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #fef3c7, #fde68a)",
  borderBottom: "2px solid #fbbf24",
  padding: theme.spacing(1.5, 3),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const DemoBannerText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.85rem",
  color: "#92400e",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const Header = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  padding: theme.spacing(3),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const HeaderTextGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "rgba(255,255,255,0.8)",
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  overflowY: "auto",
}));

const SessionCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff7ed",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
}));

const SessionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
}));

const SessionInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(2),
}));

const SessionCodeLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.7rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: theme.spacing(0.25),
}));

const SessionAvatar = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: "999px",
  background: "linear-gradient(135deg, #f97316, #ef4444)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.shadows[3],
}));

const SessionCode = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.25rem",
  marginBottom: theme.spacing(0.5),
}));

const SessionMeta = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
}));

const SessionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ParticipantStatus = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
}));

const ParticipantCount = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const CountDisplay = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: theme.palette.warning.main,
  lineHeight: 1,
}));

const CountLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

const ConnectionStatus = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1.25),
  borderRadius: theme.spacing(1),
  fontSize: "0.7rem",
  fontWeight: 500,
}));

const ShareLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const LinkBox = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1, 1.5),
  fontFamily: "monospace",
  fontSize: "0.75rem",
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
}));

const CopyButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "copied",
})(({ theme, copied }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1),
  ...(copied
    ? {
        backgroundColor: theme.palette.success.main,
        color: "#fff",
        "&:hover": {
          backgroundColor: theme.palette.success.dark,
        },
      }
    : {
        backgroundColor: theme.palette.warning.main,
        color: "#fff",
        "&:hover": {
          backgroundColor: theme.palette.warning.dark,
        },
      }),
}));

const CopyMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  fontSize: "0.75rem",
  fontWeight: 600,
  color: theme.palette.success.main,
}));

const ShareOptionsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: theme.spacing(1.5),
}));

const ShareOptionButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: theme.spacing(2),
  borderWidth: 2,
  borderStyle: "solid",
  borderColor: theme.palette.grey[200],
  padding: theme.spacing(1),
  textTransform: "none",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontSize: "0.75rem",
  "&:hover": {
    borderColor: theme.palette.warning.main,
    backgroundColor: "#fff",
  },
}));

const ShareEmoji = styled("div")(({ theme }) => ({
  fontSize: "1.5rem",
}));

const ShareText = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const WaitingWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 120,
  textAlign: "center",
  marginTop: theme.spacing(2),
}));

const Spinner = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  border: `4px solid ${theme.palette.warning.main}`,
  borderTopColor: "transparent",
  animation: "spin 1s linear infinite",
  boxSizing: "border-box",
  marginBottom: theme.spacing(1),
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

const WaitingTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.95rem",
  marginBottom: theme.spacing(0.5),
}));

const WaitingText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.5,
}));

const QRModal = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: theme.spacing(2),
}));

const QRContent = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  maxWidth: 380,
  width: "100%",
  textAlign: "center",
  position: "relative",
}));

const QRTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.1rem",
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

const QRImageWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `2px solid ${theme.palette.grey[200]}`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "& canvas": {
    maxWidth: "100%",
    height: "auto",
  },
}));

const CloseButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.primary,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5),
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: theme.palette.grey[300],
  },
}));

const RefreshButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  backgroundColor: "#fff",
  color: theme.palette.warning.main,
  borderRadius: theme.spacing(1.5),
  borderWidth: 1.5,
  borderStyle: "solid",
  borderColor: theme.palette.warning.main,
  padding: theme.spacing(0.75, 1.5),
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8rem",
  "&:hover": {
    backgroundColor: "#fff7ed",
    borderColor: theme.palette.warning.dark,
  },
}));

// NEW: Demo Mode Components
const DemoInfoBanner = styled(Box)(({ theme }) => ({
  backgroundColor: "#eff6ff",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: "2px solid #bfdbfe",
  marginBottom: theme.spacing(2),
}));

const DemoInfoText = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  color: "#1e3a8a",
  lineHeight: 1.6,
  marginBottom: theme.spacing(1),
  "&:last-of-type": {
    marginBottom: 0,
  },
}));

const StartDemoButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: "#fbbf24",
  color: "#78350f",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5, 3),
  textTransform: "none",
  fontWeight: 700,
  fontSize: "1rem",
  boxShadow: theme.shadows[4],
  "&:hover": {
    backgroundColor: "#f59e0b",
    boxShadow: theme.shadows[6],
  },
}));

// ---------- Lobby Component ----------

const Lobby = ({ sessionInfo: propSessionInfo }) => {
  const { participantCount: propParticipantCount, party_size: propPartySize } =
    propSessionInfo || {};

  // Existing state
  const [copied, setCopied] = useState(false);
  const [hasShared, setHasShared] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Session state
  const [participantCount, setParticipantCount] = useState(
    propParticipantCount || 1
  );
  const [partySize, setPartySize] = useState(propPartySize || 2);
  const [sessionStatus, setSessionStatus] = useState("pending");
  const [wsConnectionStatus, setWsConnectionStatus] = useState("connecting");
  const [lastStatusCheck, setLastStatusCheck] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs
  const wsRef = useRef(null);
  const hasNavigatedRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pollIntervalRef = useRef(null);
  const navigationAttemptedRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId: routeSessionId } = useParams();
  const posthog = usePostHog();

  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 1000;
  const POLL_INTERVAL = 2000;

  // --- Session info extraction ---
  const stateSession = location.state?.session || null;
  const stateJoinCode = location.state?.joinCode || null;
  const session = stateSession || propSessionInfo || null;
  const sessionId =
    routeSessionId ||
    (session && (session.session_id || session.sessionId)) ||
    "";
  const joinCodeRaw =
    stateJoinCode || session?.join_code || session?.joinCode || "";
  const joinCode = joinCodeRaw ? joinCodeRaw.trim().toUpperCase() : "";
  const sessionLink = joinCode
    ? `${window.location.origin}/join/${joinCode}`
    : `${window.location.origin}/`;

  // NEW: Detect demo mode
  const isDemo =
    session?.status === "demo" ||
    session?.is_demo === true ||
    propSessionInfo?.status === "demo" ||
    propSessionInfo?.is_demo === true;

  console.log("🎬 Lobby - Demo mode detected:", isDemo);
  console.log("🎬 Lobby - Session status:", session?.status);
  console.log("🎬 Lobby - Session is_demo:", session?.is_demo);

  // Track when user enters lobby
  useEffect(() => {
    posthog.capture("lobby_entered", {
      session_id: sessionId,
      is_demo: isDemo,
    });

    if (isDemo) {
      posthog.capture("demo_lobby_viewed", {
        session_id: sessionId,
      });
    }
  }, [sessionId, isDemo, posthog]);

  // Sync partySize from session object if available
  useEffect(() => {
    if (session?.party_size && session.party_size !== partySize) {
      console.log(`📊 Syncing party size from session: ${session.party_size}`);
      setPartySize(session.party_size);
    }
  }, [session, partySize]);

  // Track when partner joins (only for non-demo sessions)
  useEffect(() => {
    if (!isDemo && participantCount > 1) {
      posthog.capture("partner_joined", {
        session_id: sessionId,
        total_participants: participantCount,
      });
    }
  }, [participantCount, sessionId, isDemo, posthog]);

  // NEW: Manual demo start handler
  const handleStartDemo = () => {
    console.log("🎬 Starting demo voting");

    posthog.capture("demo_voting_started", {
      session_id: sessionId,
    });

    // Navigate directly to swipe page in demo mode
    navigate(`/session/${sessionId}/match`, {
      replace: true,
    });
  };

  // ============================================================
  // Navigation logic - SKIP for demo mode
  // ============================================================
  const attemptNavigation = (
    source,
    currentCount,
    currentSize,
    currentStatus
  ) => {
    // NEVER auto-navigate in demo mode
    if (isDemo) {
      console.log("🎬 Demo mode: Skipping auto-navigation");
      return false;
    }

    // Prevent multiple navigation attempts
    if (hasNavigatedRef.current || navigationAttemptedRef.current) {
      console.log(
        `⚠️ Navigation already attempted/completed, skipping (source: ${source})`
      );
      return false;
    }

    const hasEnoughParticipants =
      currentCount >= currentSize && currentCount > 0;

    const shouldNavigate = hasEnoughParticipants;

    console.log(`🔍 Navigation check (${source}):`, {
      status: currentStatus,
      count: currentCount,
      size: currentSize,
      hasEnoughParticipants,
      shouldNavigate,
    });

    if (shouldNavigate) {
      console.log(`🚀 Navigating to swipe page via ${source}`);

      hasNavigatedRef.current = true;
      navigationAttemptedRef.current = true;

      posthog.capture("swipe_started", {
        session_id: sessionId,
        participants: currentCount,
        navigation_method: source,
        session_status: currentStatus,
      });

      if (wsRef.current) {
        wsRef.current.close(1000, "Navigating to match page");
        wsRef.current = null;
      }

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      setTimeout(() => {
        navigate(`/session/${sessionId}/match`, {
          replace: true,
        });
      }, 100);

      return true;
    }

    return false;
  };

  // ============================================================
  // Polling - SKIP for demo mode
  // ============================================================
  const checkSessionStatus = async () => {
    if (!sessionId || !API_BASE_URL || hasNavigatedRef.current || isDemo)
      return;

    try {
      const headers = {};
      if (API_KEY) {
        headers["x-api-key"] = API_KEY;
      }

      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        headers: headers,
      });

      if (!response.ok) {
        console.error(`❌ Polling failed with status: ${response.status}`);
        return;
      }

      const data = await response.json();

      if (data.session) {
        const backendCount =
          data.session.participantCount || data.session.participant_count || 0;
        const size = data.session.party_size || 2;
        const status = data.session.status || "pending";

        let actualCount = backendCount;
        if (backendCount === 0) {
          actualCount = 1;
        }

        setParticipantCount(actualCount);
        setPartySize(size);
        setSessionStatus(status);
        setLastStatusCheck(new Date());
        setIsInitialized(true);

        attemptNavigation("polling", actualCount, size, status);
      }
    } catch (error) {
      console.error("❌ Error checking session status:", error);
    }
  };

  // ============================================================
  // Manual status check - ALLOW for demo (doesn't navigate)
  // ============================================================
  const handleManualStatusCheck = async () => {
    console.log("🔄 Manual status check triggered");
    navigationAttemptedRef.current = false;
    await checkSessionStatus();

    if (!hasNavigatedRef.current && !isDemo) {
      const remaining = partySize - participantCount;
      if (remaining > 0) {
        console.log(`⏳ Still waiting for ${remaining} more participant(s)`);
      }
    }
  };

  // ============================================================
  // WebSocket - SKIP for demo mode
  // ============================================================
  const connectWebSocket = () => {
    if (!sessionId || isDemo) {
      console.log("🎬 Demo mode or no sessionId: Skipping WebSocket setup");
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected, skipping");
      return;
    }

    setWsConnectionStatus("connecting");

    const ws = new WebSocket(getAuthenticatedWSUrl(sessionId));
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("✅ Lobby WebSocket connected");
      setWsConnectionStatus("connected");
      reconnectAttemptsRef.current = 0;

      const message = {
        action: "subscribeSession",
        sessionId,
        timestamp: new Date().toISOString(),
      };

      ws.send(JSON.stringify(message));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        const msgCount = msg.participantCount ?? msg.participant_count ?? null;
        const msgSize = msg.partySize ?? msg.party_size ?? null;
        const msgStatus = msg.status ?? null;

        if (msgCount !== null) {
          let actualCount = msgCount;
          if (msgCount === 0) {
            actualCount = 1;
          }
          setParticipantCount(actualCount);
          setIsInitialized(true);
        }

        if (msgSize !== null) {
          setPartySize(msgSize);
        }

        if (msgStatus !== null) {
          setSessionStatus(msgStatus);
        }

        const currentCount =
          msgCount !== null
            ? msgCount === 0
              ? 1
              : msgCount
            : participantCount;
        const currentSize = msgSize !== null ? msgSize : partySize;
        const currentStatus = msgStatus !== null ? msgStatus : sessionStatus;

        const isReadySignal =
          msg.type === "SESSION_READY" ||
          msg.type === "SESSION_ACTIVE" ||
          (currentCount >= currentSize && currentCount > 0);

        if (
          isReadySignal &&
          (msg.sessionId === sessionId || msg.session_id === sessionId)
        ) {
          attemptNavigation(
            "websocket",
            currentCount,
            currentSize,
            currentStatus
          );
        }
      } catch (e) {
        console.error("❌ Failed to parse WS message", e);
      }
    };

    ws.onerror = (err) => {
      console.error("❌ Lobby WebSocket error:", err);
      setWsConnectionStatus("error");
    };

    ws.onclose = (event) => {
      console.log(`🔌 Lobby WebSocket closed: ${event.code}`);
      setWsConnectionStatus("disconnected");
      wsRef.current = null;

      if (
        !hasNavigatedRef.current &&
        event.code !== 1000 &&
        reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
      ) {
        const delay =
          BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current);

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connectWebSocket();
        }, delay);
      }
    };
  };

  // ============================================================
  // Initial setup - SKIP polling/WS for demo
  // ============================================================
  useEffect(() => {
    if (!sessionId) return;

    const fetchSession = async () => {
      try {
        const headers = {};
        if (API_KEY) {
          headers["x-api-key"] = API_KEY;
        }

        const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
          headers: headers,
        });
        const data = await response.json();

        if (data.session) {
          const backendCount =
            data.session.participantCount ||
            data.session.participant_count ||
            0;
          const size = data.session.party_size || 2;
          const status = data.session.status || "pending";

          let actualCount = backendCount;
          if (backendCount === 0) {
            actualCount = 1;
          }

          setParticipantCount(actualCount);
          setPartySize(size);
          setSessionStatus(status);
          setIsInitialized(true);

          // Only auto-navigate for non-demo sessions
          if (!isDemo) {
            attemptNavigation("initial_fetch", actualCount, size, status);
          }
        }
      } catch (error) {
        console.error("❌ Error fetching session:", error);
      }
    };

    fetchSession();

    // Only start WebSocket and polling for non-demo sessions
    if (!isDemo) {
      connectWebSocket();
      pollIntervalRef.current = setInterval(checkSessionStatus, POLL_INTERVAL);
    } else {
      console.log("🎬 Demo mode: Skipping WebSocket and polling");
      setWsConnectionStatus("demo"); // Special status for demo
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
        wsRef.current = null;
      }
    };
  }, [sessionId, navigate, isDemo]);

  // ============================================================
  // Share functions
  // ============================================================
  const handleCopy = () => {
    navigator.clipboard.writeText(sessionLink);
    setCopied(true);
    setHasShared(true);
    setTimeout(() => setCopied(false), 2000);

    posthog.capture("join_code_shared", {
      session_id: sessionId,
      method: "copy",
    });
  };

  const handleShare = async () => {
    setHasShared(true);
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join my Foodie Call session!",
          text: "Let's find a place to eat together!",
          url: sessionLink,
        });

        posthog.capture("join_code_shared", {
          session_id: sessionId,
          method: "share",
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      handleCopy();
    }
  };

  const toggleQR = () => {
    setShowQR(!showQR);
    setHasShared(true);
  };

  // ============================================================
  // Connection status display - Updated for demo
  // ============================================================
  const getConnectionStatusDisplay = () => {
    if (isDemo) {
      return {
        icon: <Eye size={14} />,
        text: "Demo Mode",
        color: "#92400e",
        bg: "#fef3c7",
      };
    }

    switch (wsConnectionStatus) {
      case "connected":
        return {
          icon: <Wifi size={14} />,
          text: "Connected",
          color: "#16a34a",
          bg: "#dcfce7",
        };
      case "connecting":
        return {
          icon: <Wifi size={14} />,
          text: "Connecting...",
          color: "#ea580c",
          bg: "#ffedd5",
        };
      case "disconnected":
        return {
          icon: <WifiOff size={14} />,
          text: "Reconnecting...",
          color: "#ea580c",
          bg: "#ffedd5",
        };
      case "error":
        return {
          icon: <WifiOff size={14} />,
          text: "Connection Issue",
          color: "#dc2626",
          bg: "#fee2e2",
        };
      default:
        return {
          icon: <Wifi size={14} />,
          text: "Unknown",
          color: "#6b7280",
          bg: "#f3f4f6",
        };
    }
  };

  const connectionDisplay = getConnectionStatusDisplay();
  const isSessionReady = participantCount >= partySize;

  return (
    <>
      <Helmet>
        <title>
          {isDemo
            ? "Try Foodie Call"
            : `Join Foodie Call Session ${joinCode ? `- ${joinCode}` : ""}`}
        </title>
        <meta property="og:title" content="Join my Foodie Call session!" />
        <meta
          property="og:description"
          content="Let's find a place to eat together!"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={sessionLink} />
        <meta
          property="og:image"
          content={`${window.location.origin}/meal-mate-preview.jpg`}
        />
      </Helmet>

      <Container>
        {/* NEW: Demo Mode Banner */}
        {isDemo && (
          <DemoBanner>
            <DemoBannerText>
              <Eye size={16} />
              DEMO MODE
            </DemoBannerText>
          </DemoBanner>
        )}

        <Header>
          <HeaderRow>
            <HeaderTextGroup>
              <HeaderTitle variant="h6">Your Session is Ready!</HeaderTitle>
              <HeaderSubtitle>Share with your dining partner</HeaderSubtitle>
            </HeaderTextGroup>
          </HeaderRow>
        </Header>

        <Content>
          {/* NEW: Demo Info Banner (only for demo mode) */}
          {isDemo && (
            <DemoInfoBanner>
              <DemoInfoText sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                You're now experiencing demo mode
              </DemoInfoText>
              <DemoInfoText>
                <strong>This is what you'd normally see:</strong> In a real
                session, your partner(s) would join by clicking the link that
                you share with them. You'll wait in the lobby until all
                participants have joined, and then you'll automatically proceed
                to the voting screen together.
              </DemoInfoText>
              <DemoInfoText>
                <strong>For this demo:</strong> We'll skip the waiting and bring
                you the voting experience right away. Click below to begin.
              </DemoInfoText>
              <StartDemoButton
                fullWidth
                onClick={handleStartDemo}
                startIcon={<Eye size={18} />}
              >
                Start Demo Voting
              </StartDemoButton>
            </DemoInfoBanner>
          )}

          {/* Session Card - Show for both demo and regular */}
          <SessionCard>
            <SessionHeader>
              <SessionAvatar>
                <Users size={28} color="#ffffff" />
              </SessionAvatar>
              <SessionInfo>
                <SessionCodeLabel>Join Code</SessionCodeLabel>
                <SessionCode>{joinCode || "Preparing..."}</SessionCode>
                <SessionMeta>
                  {isDemo ? "Demo session" : "Valid for 2 hours"}
                </SessionMeta>
              </SessionInfo>
              <ConnectionStatus
                sx={{
                  backgroundColor: connectionDisplay.bg,
                  color: connectionDisplay.color,
                }}
              >
                {connectionDisplay.icon}
                <span>{connectionDisplay.text}</span>
              </ConnectionStatus>
            </SessionHeader>

            <SessionDivider />

            <ParticipantStatus>
              <ParticipantCount>
                <CountDisplay>
                  {isDemo ? "1 / 1" : `${participantCount} / ${partySize}`}
                </CountDisplay>
                <CountLabel>Participants</CountLabel>
              </ParticipantCount>

              {isDemo ? (
                <Chip
                  label="Demo Mode"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                  }}
                  size="small"
                />
              ) : isSessionReady ? (
                <Chip
                  label="Ready!"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                />
              ) : (
                <Chip
                  label={`Waiting for ${partySize - participantCount}`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                />
              )}
            </ParticipantStatus>

            <Box sx={{ mt: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={
                  isDemo
                    ? 100
                    : partySize > 0
                    ? (participantCount / partySize) * 100
                    : 0
                }
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#e5e7eb",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: isDemo
                      ? "#fbbf24"
                      : isSessionReady
                      ? "#16a34a"
                      : "#f97316",
                  },
                }}
              />
            </Box>

            {!isDemo && (
              <>
                <RefreshButton
                  fullWidth
                  size="small"
                  startIcon={<RefreshCw size={14} />}
                  onClick={handleManualStatusCheck}
                >
                  Check Status
                </RefreshButton>

                {lastStatusCheck && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                    display="block"
                    sx={{ mt: 1, fontSize: "0.65rem", opacity: 0.7 }}
                  >
                    Updated {lastStatusCheck.toLocaleTimeString()}
                  </Typography>
                )}
              </>
            )}
          </SessionCard>

          {/* Share section - Show for both, but add explanation for demo */}
          <Box>
            <ShareLabel>
              {isDemo
                ? "In a real session, you'd share this link"
                : "Share this link"}
            </ShareLabel>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <LinkBox sx={{ opacity: isDemo ? 0.6 : 1 }}>
                {sessionLink}
              </LinkBox>
              <CopyButton
                onClick={handleCopy}
                copied={copied ? 1 : 0}
                disabled={isDemo}
                sx={isDemo ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </CopyButton>
            </Stack>
            {copied && !isDemo && (
              <CopyMessage>✓ Link copied to clipboard!</CopyMessage>
            )}
            {isDemo && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  display: "block",
                }}
              >
                💡 Your partner(s) would use this link to join your session
              </Typography>
            )}
          </Box>

          <Divider>
            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.palette.text.secondary }}
            >
              {isDemo ? "Normally shared via" : "Share via"}
            </Typography>
          </Divider>

          <ShareOptionsGrid
            sx={{
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              opacity: isDemo ? 0.5 : 1,
              pointerEvents: isDemo ? "none" : "auto",
            }}
          >
            <ShareOptionButton onClick={handleShare}>
              <ShareEmoji>💬</ShareEmoji>
              <ShareText>Text Message</ShareText>
            </ShareOptionButton>
            <ShareOptionButton onClick={toggleQR}>
              <QrCode size={24} style={{ marginBottom: 4 }} />
              <ShareText>QR Code</ShareText>
            </ShareOptionButton>
          </ShareOptionsGrid>

          {showQR && !isDemo && (
            <QRModal onClick={toggleQR}>
              <QRContent onClick={(e) => e.stopPropagation()}>
                <QRTitle>Scan to Join Session</QRTitle>
                <QRImageWrapper>
                  <QRCodeCanvas
                    value={sessionLink}
                    size={256}
                    level="H"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </QRImageWrapper>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    mb: 2,
                    fontSize: "0.85rem",
                  }}
                >
                  Your partner can scan this code with their phone camera to
                  join
                </Typography>
                <CloseButton onClick={toggleQR} fullWidth>
                  Close
                </CloseButton>
              </QRContent>
            </QRModal>
          )}

          {/* Waiting Status - Show for both, with different messaging */}
          {!isDemo && !isSessionReady && (
            <WaitingWrapper>
              <Spinner />
              <WaitingTitle>
                Waiting for your partner(s) to join...
              </WaitingTitle>
              <WaitingText>
                Once your partner clicks the link, you&apos;ll both
                automatically enter the matching session together and can start
                matching!
              </WaitingText>
            </WaitingWrapper>
          )}

          {isDemo && (
            <Box
              sx={{
                textAlign: "center",
                mt: 2,
                p: 2,
                backgroundColor: "#fef3c7",
                borderRadius: 2,
                border: "1px solid #fde68a",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#92400e",
                  fontWeight: 500,
                }}
              >
                👆 Ready to see how voting works? Click "Start Demo Voting"
                above!
              </Typography>
            </Box>
          )}
        </Content>
      </Container>
    </>
  );
};

export default Lobby;
